import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { gql, useApolloClient } from '@apollo/client';
import { useRootStore } from '@/lib/providers/RootProvider';
import { readScannedLot } from '@/lib/scanning/scannedLot';
import BulkScan from './BulkScan';
import type { BulkScanInput, BulkScanMode } from './bulkScanList';

export const BULK_SCAN_CHECK = gql`
  query bulkScanCheck(
    $companyId: ID!
    $mode: BulkScanMode!
    $serialNumber: String!
    $gtin: String
    $listedSerializedItemIds: [ID!]
  ) {
    bulkScanCheck(
      companyId: $companyId
      mode: $mode
      serialNumber: $serialNumber
      gtin: $gtin
      listedSerializedItemIds: $listedSerializedItemIds
    ) {
      accepted
      refusal {
        code
        message
      }
      item {
        serializedItemId
        serialNumber
        gtin
        serializedItemType
        status
        lot
        expiration
        unitCount
        productCount
        productTitle
        ndc
        zoneId
        zoneName
        locationId
        purchaseOrderNumber
        absorbsSerializedItemIds
      }
    }
  }
`;

export const BULK_SCAN_ASSIGN = gql`
  mutation bulkScanAssign(
    $companyId: ID!
    $mode: BulkScanMode!
    $zoneId: ID!
    $items: [BulkScanAssignItem!]!
  ) {
    bulkScanAssign(companyId: $companyId, mode: $mode, zoneId: $zoneId, items: $items) {
      itemCount
      unitCount
      zoneId
      zoneName
    }
  }
`;

/** `?serialNumber=…&gtin=…&lot=…&expiration=…` — the item Scanned Options sent here, if any. */
const initialScanFrom = (searchParams: URLSearchParams): BulkScanInput | null => {
  const serialNumber = searchParams.get('serialNumber');
  if (!serialNumber) return null;
  const gtin = searchParams.get('gtin');
  return {
    serialNumber,
    gtin: gtin && gtin !== 'null' && gtin !== 'undefined' ? gtin : null,
    ...readScannedLot(searchParams)
  };
};

const BulkScanData = ({ mode }: { mode: BulkScanMode }) => {
  const client = useApolloClient();
  const companyId = useRootStore((state) => state.selectedCompany?.id) as string;
  const [searchParams] = useSearchParams();

  return (
    <BulkScan
      mode={mode}
      companyId={companyId}
      initialScan={initialScanFrom(searchParams)}
      checkScan={async (scan, listedSerializedItemIds) => {
        try {
          const { data } = await client.query({
            query: BULK_SCAN_CHECK,
            variables: {
              companyId,
              mode,
              serialNumber: scan.serialNumber,
              gtin: scan.gtin,
              listedSerializedItemIds
            },
            fetchPolicy: 'network-only'
          });
          return { check: data?.bulkScanCheck ?? null };
        } catch (error) {
          return { check: null, error };
        }
      }}
      assign={async (zoneId, items) => {
        const { data } = await client.mutate({
          mutation: BULK_SCAN_ASSIGN,
          variables: { companyId, mode, zoneId, items }
        });
        return data.bulkScanAssign;
      }}
    />
  );
};

export const BulkStock = () => <BulkScanData mode="STOCK" />;
export const BulkMove = () => <BulkScanData mode="MOVE" />;

export default BulkScanData;
